import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { formatDateForDisplay } from '../utils/dateFormat';
import './ProductSettlementsPanel.css';

const localDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const futureDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return localDate(date);
};

const ProductSettlementsPanel = ({ customerId, canManage = false, t = value => value }) => {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHandover, setShowHandover] = useState(false);
  const [editing, setEditing] = useState(null);
  const [returning, setReturning] = useState(null);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [dueDate, setDueDate] = useState(futureDate());
  const [comments, setComments] = useState('');
  const [returnQuantities, setReturnQuantities] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/product-settlements/customer/${customerId}`);
      setSettlements(response.data || []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('Unable to load product settlements.'));
    } finally {
      setLoading(false);
    }
  }, [customerId, t]);

  useEffect(() => { if (customerId) load(); }, [customerId, load]);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length < 2) return [];
    return products.filter(product =>
      `${product.stockNo || ''} ${product.productName || ''}`.toLowerCase().includes(term)
      && !selected.some(item => item.productId === product.id)
    ).slice(0, 20);
  }, [products, search, selected]);

  const loadProducts = async () => {
    if (products.length) return;
    try {
      const response = await axios.get('/api/products/all');
      setProducts(response.data || []);
    } catch (requestError) {
      setFormError(t('Unable to load products.'));
    }
  };

  const openHandover = async () => {
    setFormError('');
    setEditing(null);
    setSelected([]);
    setSearch('');
    setDueDate(futureDate());
    setComments('');
    setShowHandover(true);
    await loadProducts();
  };

  const openEdit = async settlement => {
    setFormError('');
    setEditing(settlement);
    setSelected(settlement.items.map(item => ({
      productId: item.productId,
      stockNo: item.stockNo,
      productName: item.productName,
      quantity: item.issuedQuantity,
      returnedQuantity: item.returnedQuantity
    })));
    setSearch('');
    setDueDate(settlement.dueDate);
    setComments(settlement.comments || '');
    setShowHandover(true);
    await loadProducts();
  };

  const addProduct = product => {
    setSelected(items => [...items, {
      productId: product.id,
      stockNo: product.stockNo,
      productName: product.productName,
      quantity: 1,
      returnedQuantity: 0
    }]);
    setSearch('');
  };

  const submitHandover = async event => {
    event.preventDefault();
    if (!selected.length) return setFormError(t('Select at least one product.'));
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        dueDate,
        comments,
        items: selected.map(({ productId, quantity }) => ({ productId, quantity: Number(quantity) }))
      };
      if (editing) await axios.put(`/api/product-settlements/${editing.id}`, payload);
      else await axios.post(`/api/product-settlements/customer/${customerId}`, payload);
      setShowHandover(false);
      setEditing(null);
      await load();
    } catch (requestError) {
      setFormError(requestError.response?.data?.message || requestError.response?.data || t(editing ? 'Unable to update product settlement.' : 'Unable to record product handover.'));
    } finally {
      setSaving(false);
    }
  };

  const deleteSettlement = async settlement => {
    if (!window.confirm(t('Deleting this settlement will also delete its return history. Continue?'))) return;
    setSaving(true);
    setError('');
    try {
      await axios.delete(`/api/product-settlements/${settlement.id}`);
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data || t('Unable to delete product settlement.'));
    } finally {
      setSaving(false);
    }
  };

  const closeHandover = () => {
    setShowHandover(false);
    setEditing(null);
    setFormError('');
  };

  const openReturn = settlement => {
    setReturning(settlement);
    setFormError('');
    setReturnQuantities(Object.fromEntries(settlement.items.filter(item => item.pendingQuantity > 0).map(item => [item.id, 0])));
  };

  const submitReturn = async event => {
    event.preventDefault();
    const items = Object.entries(returnQuantities)
      .filter(([, quantity]) => Number(quantity) > 0)
      .map(([settlementItemId, quantity]) => ({ settlementItemId: Number(settlementItemId), quantity: Number(quantity) }));
    if (!items.length) return setFormError(t('Enter at least one returned quantity.'));
    setSaving(true);
    setFormError('');
    try {
      await axios.post(`/api/product-settlements/${returning.id}/returns`, { items });
      setReturning(null);
      await load();
    } catch (requestError) {
      setFormError(requestError.response?.data?.message || requestError.response?.data || t('Unable to record product return.'));
    } finally {
      setSaving(false);
    }
  };

  return <div className="settlement-panel">
    <div className="settlement-header">
      <div><h3>{t('Product Settlements')}</h3><p>{t('Track products handed over now and returned later.')}</p></div>
      {canManage && <button className="btn btn-primary" onClick={openHandover}>+ {t('Record Product Handover')}</button>}
    </div>
    {error && <div className="alert alert-error">{error}</div>}
    {loading ? <div className="loading">{t('Loading product settlements...')}</div> : settlements.length === 0 ?
      <div className="settlement-empty">{t('No product settlements recorded.')}</div> :
      <div className="settlement-table-wrap"><table className="settlement-table"><thead><tr>
        <th>{t('Handover Date')}</th><th>{t('Due Date')}</th><th>{t('Products')}</th><th>{t('Progress')}</th><th>{t('Comments')}</th><th>{t('Status')}</th>{canManage && <th>{t('Action')}</th>}
      </tr></thead><tbody>{settlements.map(settlement => <tr key={settlement.id}>
        <td>{formatDateForDisplay(settlement.createdAt)}</td>
        <td>{formatDateForDisplay(settlement.dueDate)}</td>
        <td>{settlement.items.map(item => <div key={item.id}><strong>{item.productName}</strong> <small>({item.stockNo})</small> — {item.returnedQuantity}/{item.issuedQuantity} {t('returned')}</div>)}</td>
        <td>{settlement.returnedQuantity}/{settlement.totalQuantity}</td>
        <td>{settlement.comments || '-'}</td>
        <td><span className={`settlement-status ${settlement.status.toLowerCase()}`}>{settlement.status === 'SETTLED' ? t('Settled') : t('Pending')}</span></td>
        {canManage && <td>{settlement.manageable ? <div className="settlement-row-actions">
          <button className="btn btn-secondary" onClick={() => openEdit(settlement)} disabled={saving}>{t('Edit')}</button>
          {settlement.status === 'OPEN' && <button className="btn btn-secondary" onClick={() => openReturn(settlement)} disabled={saving}>{t('Record Return')}</button>}
          <button className="btn settlement-delete" onClick={() => deleteSettlement(settlement)} disabled={saving}>{t('Delete')}</button>
        </div> : '-'}</td>}
      </tr>)}</tbody></table></div>}

    {showHandover && <div className="settlement-modal-backdrop"><form className="settlement-modal" onSubmit={submitHandover}>
      <div className="settlement-modal-title"><div><h3>{t(editing ? 'Edit Product Settlement' : 'Record Product Handover')}</h3><p>{t(editing ? 'Correct the products, quantities, due date, or comments.' : 'Add every product the customer needs to return.')}</p></div><button type="button" onClick={closeHandover}>×</button></div>
      {formError && <div className="alert alert-error">{formError}</div>}
      <label>{t('Search Products')}<input value={search} onChange={event => setSearch(event.target.value)} placeholder={t('Search by product name or stock number')} /></label>
      {filteredProducts.length > 0 && <div className="settlement-search-results">{filteredProducts.map(product => <button type="button" key={product.id} onClick={() => addProduct(product)}><strong>{product.productName}</strong><small>{product.stockNo}</small></button>)}</div>}
      {selected.length > 0 && <div className="settlement-items">{selected.map(item => <div key={item.productId}><span><strong>{item.productName}</strong><small>{item.stockNo}{item.returnedQuantity > 0 ? ` · ${t('Already returned')}: ${item.returnedQuantity}` : ''}</small></span><input aria-label={`${item.productName} quantity`} type="number" min={Math.max(1, item.returnedQuantity || 0)} required value={item.quantity} onChange={event => setSelected(items => items.map(value => value.productId === item.productId ? { ...value, quantity: event.target.value } : value))} /><button type="button" disabled={item.returnedQuantity > 0} title={item.returnedQuantity > 0 ? t('Products with recorded returns cannot be removed.') : t('Remove')} onClick={() => setSelected(items => items.filter(value => value.productId !== item.productId))}>×</button></div>)}</div>}
      <div className="settlement-form-grid"><label>{t('Expected Return Date')}<input type="date" required min={editing ? undefined : localDate(new Date())} value={dueDate} onChange={event => setDueDate(event.target.value)} /></label><label>{t('Comments')}<textarea maxLength="1000" rows="3" value={comments} onChange={event => setComments(event.target.value)} placeholder={t('e.g. Replacement will be provided')} /></label></div>
      <div className="settlement-actions"><button type="button" className="btn btn-secondary" onClick={closeHandover}>{t('Cancel')}</button><button className="btn btn-primary" disabled={saving}>{saving ? t('Saving...') : t(editing ? 'Update Settlement' : 'Save Handover')}</button></div>
    </form></div>}

    {returning && <div className="settlement-modal-backdrop"><form className="settlement-modal settlement-return-modal" onSubmit={submitReturn}>
      <div className="settlement-modal-title"><div><h3>{t('Record Product Return')}</h3><p>{t('Partial returns remain pending until every quantity is received.')}</p></div><button type="button" onClick={() => setReturning(null)}>×</button></div>
      {formError && <div className="alert alert-error">{formError}</div>}
      <div className="settlement-return-list">{returning.items.filter(item => item.pendingQuantity > 0).map(item => <label key={item.id}><span><strong>{item.productName}</strong><small>{t('Pending')}: {item.pendingQuantity} · {t('Already returned')}: {item.returnedQuantity}</small></span><input type="number" min="0" max={item.pendingQuantity} value={returnQuantities[item.id] ?? 0} onChange={event => setReturnQuantities(values => ({ ...values, [item.id]: event.target.value }))} /></label>)}</div>
      <div className="settlement-actions"><button type="button" className="btn btn-secondary" onClick={() => setReturning(null)}>{t('Cancel')}</button><button className="btn btn-primary" disabled={saving}>{saving ? t('Saving...') : t('Confirm Received Products')}</button></div>
    </form></div>}
  </div>;
};

export default ProductSettlementsPanel;
